import React, { useState } from 'react';
import CommentItem from './CommentItem';
import CommentReplies from './CommentReplies';
import {
  useDeleteComment,
  useEditComment,
  useToggleReaction,
} from '../../hooks/comments/useCommentMutations';
import type { Comment, CommentTargetType, ReactionKind } from '../../types/comments';

interface CommentListProps {
  comments: Comment[];
  targetType: CommentTargetType;
  targetId: string;
  viewerId: string | null;
  viewerName: string | null;
  viewerAvatar: string | null;
  viewerRole: string | null;
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export default function CommentList({
  comments,
  targetType,
  targetId,
  viewerId,
  viewerName,
  viewerAvatar,
  viewerRole,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: CommentListProps) {
  const [openReplyFor, setOpenReplyFor] = useState<string | null>(null);

  const editTopLevel = useEditComment();
  const deleteTopLevel = useDeleteComment();
  const toggleReaction = useToggleReaction();

  if (isLoading && comments.length === 0) {
    return (
      <div className="space-y-4 pt-4">
        {[0, 1, 2].map((i) => (
          <CommentSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-sm text-error-500 py-6 text-center">
        Couldn't load comments. Please try again.
      </div>
    );
  }

  if (comments.length === 0) {
    return (
      <div className="text-sm text-text-tertiary py-8 text-center">
        Be the first to share your thoughts.
      </div>
    );
  }

  return (
    <ol className="space-y-5">
      {comments.map((c) => (
        <li key={c.id} className="space-y-3">
          <CommentItem
            comment={c}
            viewerId={viewerId}
            isTopLevel
            onReply={() => setOpenReplyFor(c.id)}
            onEdit={(next) => editTopLevel.mutate({ comment: c, body: next })}
            onDelete={() => {
              if (confirm('Delete this comment? Other learners will see “[deleted]”.')) {
                deleteTopLevel.mutate(c);
              }
            }}
            onToggleReaction={(kind: ReactionKind, emoji?: string) =>
              toggleReaction.mutate({ comment: c, kind, emoji })
            }
          />
          {(c.reply_count > 0 || openReplyFor === c.id) && (
            <CommentReplies
              parent={c}
              targetType={targetType}
              targetId={targetId}
              viewerId={viewerId}
              viewerName={viewerName}
              viewerAvatar={viewerAvatar}
              viewerRole={viewerRole}
              composerOpen={openReplyFor === c.id}
              onCloseComposer={() => setOpenReplyFor(null)}
            />
          )}
        </li>
      ))}
      {hasNextPage && (
        <li className="pt-2">
          <button
            type="button"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
            className="inline-flex items-center justify-center w-full h-9 px-4 rounded-full text-sm font-semibold text-primary-500 hover:bg-primary-500/10 disabled:opacity-50 transition-[background-color] duration-fast ease-out-strong"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more comments'}
          </button>
        </li>
      )}
    </ol>
  );
}

function CommentSkeleton() {
  return (
    <div className="flex gap-3 items-start animate-pulse">
      <div className="w-8 h-8 rounded-full bg-text-tertiary/15 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-40 rounded bg-text-tertiary/15" />
        <div className="h-3 w-3/4 rounded bg-text-tertiary/10" />
        <div className="h-3 w-1/2 rounded bg-text-tertiary/10" />
      </div>
    </div>
  );
}
